import { useState } from 'react';
import { Alert, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { createCustomer, getCustomers } from '@/features/customers/api';
import { fieldErrorsFrom, validateCustomerForm } from '@/features/customers/customerForm';
import { customerStatusLabels } from '@/features/operations/labels';
import type { CustomerFieldErrors, CustomerItem } from '@/features/customers/types';

/** Shared counter buyer picker. The operation sends only an id; the server freezes account data. */
export function BuyerSelector({
  token,
  buyer,
  onChange,
  requirePhone = false,
}: {
  token: string;
  buyer: CustomerItem | null;
  onChange: (buyer: CustomerItem | null) => void;
  requirePhone?: boolean;
}) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<CustomerItem[]>([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [fieldErrors, setFieldErrors] = useState<CustomerFieldErrors>({});

  const find = async () => {
    if (!search.trim()) return;
    setLoading(true);
    setSearched(false);
    setError(null);
    try {
      const page = await getCustomers(token, { search: search.trim() });
      setResults(page.customers);
      setSearched(true);
    } catch {
      setError('No pudimos buscar cuentas. Volvé a intentar.');
    } finally {
      setLoading(false);
    }
  };

  const create = async () => {
    const problems = validateCustomerForm(
      { fullName, email, phone },
      { phoneRequired: requirePhone },
    );
    setFieldErrors(problems);
    if (Object.keys(problems).length) return;
    setLoading(true);
    setError(null);
    try {
      const result = await createCustomer(token, { fullName, email, phone });
      if (requirePhone && !result.customer.phone) {
        setError('Esa cuenta no tiene teléfono. Completalo desde su ficha antes de reservar.');
        return;
      }
      onChange(result.customer);
      setCreating(false);
      setResults([]);
    } catch (cause) {
      const marked = fieldErrorsFrom(cause);
      if (marked) setFieldErrors(marked);
      else setError('No pudimos crear o encontrar la cuenta. Volvé a intentar.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">Cuenta compradora</Typography>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {buyer ? (
        <Alert severity="info">
          {buyer.fullName} · {buyer.email} · {customerStatusLabels[buyer.status]}
          {' · '}
          <Link
            component={RouterLink}
            to={`/admin/customers/${buyer.id}`}
            target="_blank"
            rel="noopener"
          >
            Ver ficha
          </Link>
          {' · '}
          <Button size="small" onClick={() => onChange(null)}>
            Cambiar
          </Button>
        </Alert>
      ) : (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              fullWidth
              label="Buscar por nombre, mail o teléfono"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setSearched(false);
                setResults([]);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void find();
                }
              }}
            />
            <Button
              variant="outlined"
              disabled={loading || !search.trim()}
              onClick={() => void find()}
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </Button>
          </Stack>
          {results.length === 0 && searched && !loading ? (
            <Alert severity="info">
              No hay cuentas en los resultados. Podés crear una pendiente.
            </Alert>
          ) : null}
          {results.map((customer) => (
            <Button
              key={customer.id}
              variant="outlined"
              disabled={requirePhone && !customer.phone}
              onClick={() => onChange(customer)}
              sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
            >
              {customer.fullName} · {customer.email} · {customer.phone || 'Sin teléfono'} ·{' '}
              {customerStatusLabels[customer.status]}
            </Button>
          ))}
          {requirePhone && results.some((customer) => !customer.phone) ? (
            <Typography variant="caption">
              Para reservar, completá el teléfono desde la ficha de la cuenta.
            </Typography>
          ) : null}
          <Button onClick={() => setCreating((value) => !value)}>
            {creating ? 'Cerrar alta rápida' : 'Crear cuenta pendiente'}
          </Button>
          {creating ? (
            <Stack spacing={1}>
              <TextField
                required
                label="Nombre"
                value={fullName}
                error={Boolean(fieldErrors.fullName)}
                helperText={fieldErrors.fullName}
                onChange={(event) => {
                  setFullName(event.target.value);
                  setFieldErrors((current) => ({ ...current, fullName: undefined }));
                }}
              />
              <TextField
                required
                type="email"
                label="Mail"
                value={email}
                error={Boolean(fieldErrors.email)}
                helperText={fieldErrors.email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setFieldErrors((current) => ({ ...current, email: undefined }));
                }}
              />
              <TextField
                required={requirePhone}
                type="tel"
                label="Teléfono"
                value={phone}
                error={Boolean(fieldErrors.phone)}
                helperText={
                  fieldErrors.phone ||
                  (requirePhone
                    ? 'La reserva necesita un teléfono de contacto.'
                    : 'Opcional para una venta directa.')
                }
                onChange={(event) => {
                  setPhone(event.target.value);
                  setFieldErrors((current) => ({ ...current, phone: undefined }));
                }}
              />
              <Typography variant="body2">
                Si el mail ya existe, usaremos esa cuenta. Si es nueva, enviaremos una invitación.
              </Typography>
              <Button variant="outlined" disabled={loading} onClick={() => void create()}>
                {loading ? 'Guardando...' : 'Crear y seleccionar'}
              </Button>
            </Stack>
          ) : null}
        </>
      )}
    </Stack>
  );
}
